import { z } from 'zod';
import { TASK_STATUSES, type SystemEntity } from '@erp/shared-types';
import {
  TASK_PAGE_MAX_PAGE_SIZE,
  type ListTasksInput,
  type TaskAnchorReferenceFilter,
} from '@erp/rpc';
import { queueShowsSnoozed } from './taskSnooze';

// Pure URL/filter logic for the tasks queue page, kept free of React imports
// so it stays trivially unit-testable (mirrors collectionThreadFilterPrefs).

/** Stable key for this table's saved column + filter preference rows. */
export const TASKS_QUEUE_TABLE_ID = 'tasks-queue';

export const TASKS_QUEUE_DEFAULT_PAGE_SIZE = 25;

/** Sort fields the tasks.list contract accepts. */
export const TASK_QUEUE_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'dueDate',
  'priority',
  'status',
  'title',
] as const;

/**
 * URL search params for `/tasks` — the queue is URL-parameterized
 * (`?type=<key>&status=…`), so the URL is the shareable source of truth.
 * `type` carries the task type KEY (readable URLs); the page resolves it to
 * the type id via `taskTypes.list`.
 */
export const tasksQueueSearchSchema = z.object({
  type: z.string().optional(),
  status: z.array(z.enum(TASK_STATUSES)).optional(),
  /** 'mine' | 'unassigned' | 'user:<id>' | 'agent:<id>' */
  assignee: z.string().optional(),
  outcome: z.string().optional(),
  search: z.string().optional(),
  /**
   * Anchor reference filters (task pages): anchor path → selected record id,
   * e.g. `{ supplierId: '<uuid>' }`. Only paths of visible reference columns
   * reach `tasks.list` (see `buildAnchorReferenceFilters`).
   */
  ref: z.record(z.string(), z.string()).optional(),
  /** When true, the queue includes tasks that are still snoozed. */
  showSnoozed: z.boolean().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(TASK_PAGE_MAX_PAGE_SIZE)
    .optional(),
  sortBy: z.enum(TASK_QUEUE_SORT_FIELDS).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export type TasksQueueSearch = z.infer<typeof tasksQueueSearchSchema>;

/**
 * The subset of the queue's view state remembered across sessions per user.
 * Page position is deliberately excluded. Module-scope so the reference is
 * stable for `useTableFilterPreferences`' parse cache.
 */
export const TaskQueueFilterPrefsSchema = z.object({
  type: z.string().optional(),
  status: z.array(z.enum(TASK_STATUSES)).optional(),
  assignee: z.string().optional(),
  outcome: z.string().optional(),
  pageSize: z.number().int().min(1).max(TASK_PAGE_MAX_PAGE_SIZE).optional(),
  sortBy: z.enum(TASK_QUEUE_SORT_FIELDS).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export type TaskQueueFilterPrefs = z.infer<typeof TaskQueueFilterPrefsSchema>;

/**
 * Search text is deliberately NOT persisted — restoring a stale query on a
 * fresh visit is confusing, and the seed path can't repopulate the debounced
 * input without fighting in-flight typing. Deep links with `?search=` work.
 */
export const TASK_QUEUE_PERSISTED_FILTER_KEYS = [
  'type',
  'status',
  'assignee',
  'outcome',
  'pageSize',
  'sortBy',
  'sortOrder',
] as const satisfies readonly (keyof TaskQueueFilterPrefs)[];

/**
 * Project the current search params down to the persisted-filter shape, in a
 * fixed key order and dropping empty values, so `JSON.stringify` works as an
 * equality check when deciding whether the saved row needs to change.
 */
export function buildTaskQueueFilterPrefs(
  params: Partial<TaskQueueFilterPrefs>,
): TaskQueueFilterPrefs {
  const prefs: TaskQueueFilterPrefs = {};
  if (params.type) prefs.type = params.type;
  if (params.status?.length) prefs.status = params.status;
  if (params.assignee) prefs.assignee = params.assignee;
  if (params.outcome) prefs.outcome = params.outcome;
  if (params.pageSize) prefs.pageSize = params.pageSize;
  if (params.sortBy) prefs.sortBy = params.sortBy;
  if (params.sortOrder) prefs.sortOrder = params.sortOrder;
  return prefs;
}

/**
 * The task-page default-filter pattern is case-insensitive, so a stored
 * preset may arrive as "UNASSIGNED". Other presets stay untouched: user and
 * agent ids are case-sensitive.
 */
function applyQueueAssignee(
  input: ListTasksInput,
  assignee: string | undefined,
  currentUserId: string | undefined,
): void {
  const normalized =
    assignee?.toLowerCase() === 'unassigned' ? 'unassigned' : assignee;
  if (normalized === 'unassigned') {
    input.unassigned = true;
    return;
  }
  if (normalized === 'mine') {
    if (currentUserId) input.assigneeUserId = currentUserId;
    return;
  }
  if (normalized?.startsWith('user:')) {
    input.assigneeUserId = normalized.slice('user:'.length);
    return;
  }
  if (normalized?.startsWith('agent:')) {
    input.assigneeAgentDefinitionId = normalized.slice('agent:'.length);
  }
}

/**
 * Translate the queue's URL params into the `tasks.list` input. The caller
 * resolves the type key to its id (`taskTypeId`) and supplies the current
 * user id for the 'mine' assignee preset.
 */
export function buildTaskListInput(
  search: TasksQueueSearch,
  opts: {
    currentUserId?: string;
    taskTypeId?: string;
    /** The type's anchor entity; `ref` picks only filter when it is known. */
    anchorEntityType?: SystemEntity | null;
  },
): ListTasksInput {
  const pageSize = search.pageSize ?? TASKS_QUEUE_DEFAULT_PAGE_SIZE;
  const page = search.page ?? 1;

  const input: ListTasksInput = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    sortBy: search.sortBy ?? 'updatedAt',
    sortOrder: search.sortOrder ?? 'desc',
  };

  if (opts.taskTypeId) input.taskTypeId = opts.taskTypeId;
  if (search.status && search.status.length > 0) {
    input.statuses = search.status;
  }
  if (search.outcome) input.outcome = search.outcome;
  if (search.search?.trim()) input.search = search.search.trim();
  if (!queueShowsSnoozed(search)) input.excludeSnoozed = true;
  applyQueueAssignee(input, search.assignee, opts.currentUserId);
  applyAnchorReferenceFilters(input, search.ref, opts.anchorEntityType);

  return input;
}

/**
 * `?ref` picks (anchor path → record id) as `tasks.list` reference filters.
 * The server checks each path is a reference field of the anchor type.
 */
function applyAnchorReferenceFilters(
  input: ListTasksInput,
  ref: TasksQueueSearch['ref'],
  anchorEntityType: SystemEntity | null | undefined,
): void {
  if (!anchorEntityType || !ref) return;
  const filters: TaskAnchorReferenceFilter[] = [];
  for (const [path, id] of Object.entries(ref)) {
    if (id) filters.push({ path, ids: [id] });
  }
  if (filters.length === 0) return;
  input.anchorEntityType = anchorEntityType;
  input.anchorReferenceFilters = filters;
}
